"use client"
import { useState, useEffect, useCallback } from "react"
import { useNavigate } from "react-router-dom"
import { ChevronLeft, Search, Check, X, Users, Shield, Mail, BarChart3, Ban, Send } from "lucide-react"
import TokenChecker from "../TokenChecker"

const API_PREFIX = "/api/flowsync/admin/"

const STATUS_BADGE = {
  pending: "bg-yellow-50 text-yellow-700 border-yellow-200",
  approved: "bg-green-50 text-green-700 border-green-200",
  denied: "bg-red-50 text-red-700 border-red-200",
}

export default function AdminPanel() {
  const navigate = useNavigate()
  const [authorized, setAuthorized] = useState(null)
  const [stats, setStats] = useState(null)
  const [activeTab, setActiveTab] = useState("beta")

  // Beta requests state
  const [requests, setRequests] = useState([])
  const [statusFilter, setStatusFilter] = useState("")
  const [search, setSearch] = useState("")
  const [loading, setLoading] = useState(true)
  const [actionMessage, setActionMessage] = useState(null)
  const [actionError, setActionError] = useState(null)
  const [processingIds, setProcessingIds] = useState(new Set())

  // Users state
  const [users, setUsers] = useState([])
  const [userSearch, setUserSearch] = useState("")
  const [userActionMessage, setUserActionMessage] = useState(null)
  const [userActionError, setUserActionError] = useState(null)
  const [processingUserIds, setProcessingUserIds] = useState(new Set())
  const [confirmingTerminate, setConfirmingTerminate] = useState(null)

  // Email modal state
  const [emailModal, setEmailModal] = useState(null)
  const [emailSubject, setEmailSubject] = useState("")
  const [emailBody, setEmailBody] = useState("")
  const [emailSending, setEmailSending] = useState(false)
  const [emailError, setEmailError] = useState(null)

  const fetchStats = useCallback(async () => {
    try {
      const res = await TokenChecker(`${API_PREFIX}stats/`, { method: "GET" })
      if (res.status === 403) { setAuthorized(false); return }
      if (!res.ok) throw new Error("Failed to load stats")
      setAuthorized(true)
      setStats(await res.json())
    } catch {
      setAuthorized(false)
    }
  }, [])

  const fetchRequests = useCallback(async () => {
    try {
      const params = new URLSearchParams()
      if (statusFilter) params.set("status", statusFilter)
      if (search.trim()) params.set("search", search.trim())
      const qs = params.toString()
      const res = await TokenChecker(`${API_PREFIX}beta-requests/${qs ? `?${qs}` : ""}`, { method: "GET" })
      if (!res.ok) return
      const data = await res.json()
      setRequests(data.requests || [])
    } catch { /* silently fail */ }
  }, [statusFilter, search])

  const fetchUsers = useCallback(async () => {
    try {
      const params = new URLSearchParams()
      if (userSearch.trim()) params.set("search", userSearch.trim())
      const qs = params.toString()
      const res = await TokenChecker(`${API_PREFIX}users/${qs ? `?${qs}` : ""}`, { method: "GET" })
      if (!res.ok) return
      const data = await res.json()
      setUsers(data.users || [])
    } catch { /* silently fail */ }
  }, [userSearch])

  useEffect(() => {
    const init = async () => {
      setLoading(true)
      await fetchStats()
      await fetchRequests()
      setLoading(false)
    }
    init()
  }, [fetchStats, fetchRequests])

  useEffect(() => {
    if (authorized === false) navigate("/home")
  }, [authorized, navigate])

  useEffect(() => {
    if (activeTab === "users" && authorized) fetchUsers()
  }, [activeTab, authorized, fetchUsers])

  const handleApprove = async (id) => {
    setActionMessage(null); setActionError(null)
    setProcessingIds((prev) => new Set(prev).add(id))
    try {
      const res = await TokenChecker(`${API_PREFIX}beta-requests/${id}/approve/`, { method: "POST" })
      const data = await res.json().catch(() => ({}))
      if (!res.ok) throw new Error(data.error || "Failed to approve")
      setRequests((prev) => prev.map((r) => r.id === id ? { ...r, status: "approved", approved_at: new Date().toISOString() } : r))
      fetchStats()
      if (data.email_sent) {
        setActionMessage(`${data.message} Notification email sent.`)
      } else {
        setActionError(`${data.message} Email failed: ${data.email_error || "unknown error"}`)
      }
    } catch (err) {
      setActionError(err?.response?.data?.error || err.message)
    } finally {
      setProcessingIds((prev) => { const next = new Set(prev); next.delete(id); return next })
    }
  }

  const handleDeny = async (id) => {
    setActionMessage(null); setActionError(null)
    setProcessingIds((prev) => new Set(prev).add(id))
    try {
      const res = await TokenChecker(`${API_PREFIX}beta-requests/${id}/deny/`, { method: "POST" })
      const data = await res.json().catch(() => ({}))
      if (!res.ok) throw new Error(data.error || "Failed to deny")
      setRequests((prev) => prev.map((r) => r.id === id ? { ...r, status: "denied" } : r))
      fetchStats()
      setActionMessage(data.message)
    } catch (err) {
      setActionError(err?.response?.data?.error || err.message)
    } finally {
      setProcessingIds((prev) => { const next = new Set(prev); next.delete(id); return next })
    }
  }

  const handleTerminate = async (userId) => {
    setProcessingUserIds((prev) => new Set(prev).add(userId))
    setUserActionMessage(null); setUserActionError(null)
    try {
      const res = await TokenChecker(`${API_PREFIX}users/${userId}/terminate/`, { method: "POST" })
      const data = await res.json().catch(() => ({}))
      if (!res.ok) throw new Error(data.error || "Failed to terminate")
      setUsers((prev) => prev.map((u) => u.id === userId ? { ...u, is_active: false } : u))
      setUserActionMessage(data.message)
      fetchStats()
    } catch (err) {
      setUserActionError(err?.response?.data?.error || err.message)
    } finally {
      setProcessingUserIds((prev) => { const next = new Set(prev); next.delete(userId); return next })
      setConfirmingTerminate(null)
    }
  }

  const handleSendEmail = async () => {
    if (!emailModal) return
    setEmailSending(true); setEmailError(null)
    try {
      const res = await TokenChecker(`${API_PREFIX}users/${emailModal.id}/email/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ subject: emailSubject, message: emailBody }),
      })
      const data = await res.json().catch(() => ({}))
      if (!res.ok) throw new Error(data.error || "Failed to send email")
      setUserActionMessage(data.message)
      setEmailModal(null); setEmailSubject(""); setEmailBody("")
    } catch (err) {
      setEmailError(err?.response?.data?.error || err.message)
    } finally {
      setEmailSending(false)
    }
  }

  if (loading || authorized === null) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <p className="text-gray-500">Loading admin panel...</p>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Email modal */}
      {emailModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg mx-4 p-6 space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-base font-semibold text-gray-900">Email {emailModal.email}</h3>
              <button onClick={() => { setEmailModal(null); setEmailSubject(""); setEmailBody(""); setEmailError(null) }} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            <input
              type="text"
              value={emailSubject}
              onChange={(e) => setEmailSubject(e.target.value)}
              placeholder="Subject"
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-100"
            />
            <textarea
              value={emailBody}
              onChange={(e) => setEmailBody(e.target.value)}
              placeholder="Message"
              rows={6}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-100 resize-none"
            />
            {emailError && <p className="text-xs text-red-600">{emailError}</p>}
            <div className="flex justify-end gap-3">
              <button
                onClick={() => { setEmailModal(null); setEmailSubject(""); setEmailBody(""); setEmailError(null) }}
                className="px-4 py-2 text-sm text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSendEmail}
                disabled={emailSending || !emailSubject.trim() || !emailBody.trim()}
                className="inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold rounded-lg bg-blue-500 text-white hover:bg-blue-600 disabled:opacity-50 transition-colors"
              >
                <Send className="w-4 h-4" />
                {emailSending ? "Sending..." : "Send"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <header className="bg-white border-b border-gray-200 px-8 py-6">
        <div className="flex items-center justify-between">
          <div>
            <span className="text-3xl font-extrabold tracking-tight bg-gradient-to-r from-[#10A7F3] to-[#10F3D6] bg-clip-text text-transparent">
              <ChevronLeft
                className="inline-block w-6 h-6 mr-4 cursor-pointer text-[#10A7F3]"
                onClick={() => navigate("/home")}
              />
              Admin Panel
            </span>
            <p className="text-sm ml-8 text-gray-500">Manage FlowSync beta access and users</p>
          </div>
          <div className="flex items-center gap-2">
            <Shield className="w-5 h-5 text-blue-500" />
            <span className="text-sm font-medium text-gray-600">Staff Access</span>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-8 py-8 space-y-8">
        {/* Stats cards */}
        {stats && (
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-4">
            <StatCard icon={Users} label="Total Users" value={stats.total_users} color="blue" />
            <StatCard icon={Mail} label="Pending" value={stats.beta_pending} color="yellow" />
            <StatCard icon={Check} label="Approved" value={stats.beta_approved} color="green" />
            <StatCard icon={X} label="Denied" value={stats.beta_denied} color="red" />
            <StatCard icon={Mail} label="Active Gmail" value={stats.active_accounts} color="cyan" />
            <StatCard icon={BarChart3} label="Total Scans" value={stats.total_scans} color="purple" />
            <StatCard icon={BarChart3} label="Today" value={stats.scans_today} color="indigo" />
          </div>
        )}

        {/* Tabs */}
        <div className="flex border-b border-gray-200">
          <button
            onClick={() => setActiveTab("beta")}
            className={`px-5 py-3 text-sm font-medium border-b-2 transition-colors ${activeTab === "beta" ? "border-blue-500 text-blue-600" : "border-transparent text-gray-500 hover:text-gray-700"}`}
          >
            Beta Requests
          </button>
          <button
            onClick={() => setActiveTab("users")}
            className={`px-5 py-3 text-sm font-medium border-b-2 transition-colors ${activeTab === "users" ? "border-blue-500 text-blue-600" : "border-transparent text-gray-500 hover:text-gray-700"}`}
          >
            Users
          </button>
        </div>

        {/* Action messages — beta tab */}
        {activeTab === "beta" && actionMessage && (
          <div className="rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-700">{actionMessage}</div>
        )}
        {activeTab === "beta" && actionError && (
          <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{actionError}</div>
        )}

        {/* Action messages — users tab */}
        {activeTab === "users" && userActionMessage && (
          <div className="rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-700">{userActionMessage}</div>
        )}
        {activeTab === "users" && userActionError && (
          <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{userActionError}</div>
        )}

        {/* Beta requests table */}
        {activeTab === "beta" && (
          <section className="bg-white rounded-lg border border-gray-200 shadow-sm">
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex flex-wrap items-center justify-between gap-4">
                <h2 className="text-lg font-semibold text-gray-900">Beta Access Requests</h2>
                <div className="flex items-center gap-3">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                      type="text"
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      placeholder="Search by email..."
                      className="pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-100 w-56"
                    />
                  </div>
                  <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    className="text-sm border border-gray-200 rounded-lg px-3 py-2 focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-100"
                  >
                    <option value="">All statuses</option>
                    <option value="pending">Pending</option>
                    <option value="approved">Approved</option>
                    <option value="denied">Denied</option>
                  </select>
                </div>
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-100 bg-gray-50">
                    <th className="px-6 py-3 text-left font-semibold text-gray-600">Email</th>
                    <th className="px-6 py-3 text-left font-semibold text-gray-600">User</th>
                    <th className="px-6 py-3 text-left font-semibold text-gray-600">Status</th>
                    <th className="px-6 py-3 text-left font-semibold text-gray-600">Requested</th>
                    <th className="px-6 py-3 text-left font-semibold text-gray-600">Approved</th>
                    <th className="px-6 py-3 text-right font-semibold text-gray-600">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {requests.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="px-6 py-12 text-center text-gray-400">No beta requests found.</td>
                    </tr>
                  ) : (
                    requests.map((req) => {
                      const isProcessing = processingIds.has(req.id)
                      return (
                        <tr key={req.id} className="border-b border-gray-50 hover:bg-gray-50/50">
                          <td className="px-6 py-4 font-medium text-gray-900">{req.email}</td>
                          <td className="px-6 py-4 text-gray-500">{req.username || "-"}</td>
                          <td className="px-6 py-4">
                            <span className={`inline-block px-2.5 py-1 text-xs font-semibold rounded-full border ${STATUS_BADGE[req.status] || "bg-gray-50 text-gray-600 border-gray-200"}`}>
                              {req.status}
                            </span>
                          </td>
                          <td className="px-6 py-4 text-gray-500">
                            {req.requested_at ? new Date(req.requested_at).toLocaleDateString() : "-"}
                          </td>
                          <td className="px-6 py-4 text-gray-500">
                            {req.approved_at ? new Date(req.approved_at).toLocaleDateString() : "-"}
                          </td>
                          <td className="px-6 py-4 text-right">
                            <div className="flex items-center justify-end gap-2">
                              {req.status !== "approved" && (
                                <button
                                  onClick={() => handleApprove(req.id)}
                                  disabled={isProcessing}
                                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-md bg-green-50 text-green-700 border border-green-200 hover:bg-green-100 disabled:opacity-50 transition-colors"
                                >
                                  <Check className="w-3.5 h-3.5" />
                                  Approve
                                </button>
                              )}
                              {req.status !== "denied" && (
                                <button
                                  onClick={() => handleDeny(req.id)}
                                  disabled={isProcessing}
                                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-md bg-red-50 text-red-700 border border-red-200 hover:bg-red-100 disabled:opacity-50 transition-colors"
                                >
                                  <X className="w-3.5 h-3.5" />
                                  Deny
                                </button>
                              )}
                              {req.status === "approved" && (
                                <span className="text-xs text-gray-400">Approved</span>
                              )}
                            </div>
                          </td>
                        </tr>
                      )
                    })
                  )}
                </tbody>
              </table>
            </div>
          </section>
        )}

        {/* Users table */}
        {activeTab === "users" && (
          <section className="bg-white rounded-lg border border-gray-200 shadow-sm">
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex flex-wrap items-center justify-between gap-4">
                <h2 className="text-lg font-semibold text-gray-900">All Users</h2>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="text"
                    value={userSearch}
                    onChange={(e) => setUserSearch(e.target.value)}
                    placeholder="Search by name or email..."
                    className="pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-100 w-64"
                  />
                </div>
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-100 bg-gray-50">
                    <th className="px-6 py-3 text-left font-semibold text-gray-600">Name</th>
                    <th className="px-6 py-3 text-left font-semibold text-gray-600">Email</th>
                    <th className="px-6 py-3 text-left font-semibold text-gray-600">Joined</th>
                    <th className="px-6 py-3 text-left font-semibold text-gray-600">Last Login</th>
                    <th className="px-6 py-3 text-left font-semibold text-gray-600">Status</th>
                    <th className="px-6 py-3 text-left font-semibold text-gray-600">Beta</th>
                    <th className="px-6 py-3 text-right font-semibold text-gray-600">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {users.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="px-6 py-12 text-center text-gray-400">No users found.</td>
                    </tr>
                  ) : (
                    users.map((u) => {
                      const isProcessing = processingUserIds.has(u.id)
                      const isConfirming = confirmingTerminate === u.id
                      const displayName = [u.first_name, u.last_name].filter(Boolean).join(" ") || u.username
                      return (
                        <tr key={u.id} className="border-b border-gray-50 hover:bg-gray-50/50">
                          <td className="px-6 py-4 font-medium text-gray-900">
                            {displayName}
                            {u.is_staff && (
                              <span className="ml-2 text-xs bg-indigo-50 text-indigo-600 border border-indigo-200 rounded-full px-1.5 py-0.5">staff</span>
                            )}
                          </td>
                          <td className="px-6 py-4 text-gray-500">{u.email || "-"}</td>
                          <td className="px-6 py-4 text-gray-500">
                            {new Date(u.date_joined).toLocaleDateString()}
                          </td>
                          <td className="px-6 py-4 text-gray-500">
                            {u.last_login ? new Date(u.last_login).toLocaleDateString() : "Never"}
                          </td>
                          <td className="px-6 py-4">
                            {u.is_active ? (
                              <span className="inline-block px-2.5 py-1 text-xs font-semibold rounded-full border bg-green-50 text-green-700 border-green-200">Active</span>
                            ) : (
                              <span className="inline-block px-2.5 py-1 text-xs font-semibold rounded-full border bg-red-50 text-red-700 border-red-200">Terminated</span>
                            )}
                          </td>
                          <td className="px-6 py-4">
                            {u.beta_status ? (
                              <span className={`inline-block px-2.5 py-1 text-xs font-semibold rounded-full border ${STATUS_BADGE[u.beta_status] || "bg-gray-50 text-gray-600 border-gray-200"}`}>
                                {u.beta_status}
                              </span>
                            ) : (
                              <span className="text-gray-400">-</span>
                            )}
                          </td>
                          <td className="px-6 py-4 text-right">
                            <div className="flex items-center justify-end gap-2">
                              <button
                                onClick={() => { setEmailModal(u); setEmailError(null) }}
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-md bg-blue-50 text-blue-700 border border-blue-200 hover:bg-blue-100 transition-colors"
                              >
                                <Mail className="w-3.5 h-3.5" />
                                Email
                              </button>
                              {u.is_active && !u.is_staff && (
                                isConfirming ? (
                                  <div className="flex items-center gap-1.5">
                                    <span className="text-xs text-gray-500">Sure?</span>
                                    <button
                                      onClick={() => handleTerminate(u.id)}
                                      disabled={isProcessing}
                                      className="px-2.5 py-1.5 text-xs font-semibold rounded-md bg-red-500 text-white hover:bg-red-600 disabled:opacity-50 transition-colors"
                                    >
                                      Yes
                                    </button>
                                    <button
                                      onClick={() => setConfirmingTerminate(null)}
                                      className="px-2.5 py-1.5 text-xs font-semibold rounded-md bg-gray-100 text-gray-600 hover:bg-gray-200 transition-colors"
                                    >
                                      No
                                    </button>
                                  </div>
                                ) : (
                                  <button
                                    onClick={() => setConfirmingTerminate(u.id)}
                                    disabled={isProcessing}
                                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-md bg-red-50 text-red-700 border border-red-200 hover:bg-red-100 disabled:opacity-50 transition-colors"
                                  >
                                    <Ban className="w-3.5 h-3.5" />
                                    Terminate
                                  </button>
                                )
                              )}
                            </div>
                          </td>
                        </tr>
                      )
                    })
                  )}
                </tbody>
              </table>
            </div>
          </section>
        )}
      </main>
    </div>
  )
}

function StatCard({ icon: Icon, label, value, color }) {
  const colorMap = {
    blue: "bg-blue-50 text-blue-700",
    yellow: "bg-yellow-50 text-yellow-700",
    green: "bg-green-50 text-green-700",
    red: "bg-red-50 text-red-700",
    cyan: "bg-cyan-50 text-cyan-700",
    purple: "bg-purple-50 text-purple-700",
    indigo: "bg-indigo-50 text-indigo-700",
  }
  const classes = colorMap[color] || colorMap.blue

  return (
    <div className={`rounded-lg px-4 py-3 ${classes}`}>
      <div className="flex items-center gap-2 mb-1">
        <Icon className="w-4 h-4 opacity-70" />
        <span className="text-xs font-medium opacity-80">{label}</span>
      </div>
      <p className="text-2xl font-semibold">{value ?? 0}</p>
    </div>
  )
}
